import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import "leaflet/dist/leaflet.css";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { GISProperty } from "../types/gis";
import { useNavigate } from "react-router-dom";

export default function GisManager() {
  const [properties, setProperties] = useState<GISProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({ open: false, message: "", severity: "success" });
  const [uploadingKml, setUploadingKml] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [showCoordinatesForm, setShowCoordinatesForm] = useState(false);
  const [newCoordinatesProperty, setNewCoordinatesProperty] = useState({
    parcel_id: "",
    geometry: "",
  });
  const [editingProperty, setEditingProperty] = useState<GISProperty | null>(
    null
  );
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [sourceParcelId, setSourceParcelId] = useState("");
  const [destinationParcelId, setDestinationParcelId] = useState("");
  const navigate = useNavigate();

  const fetchProperties = async () => {
    setLoading(true);
    try {
      console.log("Fetching GIS properties...");
      const res = await fetch("/api/gis/");
      console.log("GIS API response status:", res.status);
      if (!res.ok) {
        const errorText = await res.text();
        console.error("Error response:", errorText);
        throw new Error(`Failed to fetch properties: ${errorText}`);
      }
      const data = await res.json();
      console.log("Fetched GIS properties:", data);
      if (!Array.isArray(data)) {
        console.error("Invalid data format:", data);
        throw new Error("Invalid data format received from server");
      }
      setProperties(data);
    } catch (e: any) {
      console.error("Error fetching GIS properties:", e);
      setError(e.message || "Error loading properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("GisManager component mounted");
    fetchProperties();
  }, []);

  const handleDelete = async (parcelId: string) => {
    try {
      const res = await fetch(`/api/gis/${parcelId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete property");
      setSnackbar({
        open: true,
        message: "Property deleted successfully",
        severity: "success",
      });
      fetchProperties();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.message || "Error deleting property",
        severity: "error",
      });
    }
  };

  const handleEdit = (property: GISProperty) => {
    setEditingProperty(property);
  };

  const handleSave = async () => {
    if (!editingProperty) return;

    try {
      const res = await fetch(`/api/gis/${editingProperty.parcel_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editingProperty),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to update property");
      }

      setSnackbar({
        open: true,
        message: "Property updated successfully",
        severity: "success",
      });
      setEditingProperty(null);
      fetchProperties();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.message || "Error updating property",
        severity: "error",
      });
    }
  };

  const handleLinkGeometry = async () => {
    if (!sourceParcelId || !destinationParcelId) return;

    try {
      const res = await fetch("/api/gis/link-geometry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_parcel_id: sourceParcelId,
          destination_parcel_id: destinationParcelId,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to link geometry");
      }

      setSnackbar({
        open: true,
        message: "Geometry linked successfully",
        severity: "success",
      });
      setShowLinkDialog(false);
      setSourceParcelId("");
      setDestinationParcelId("");
      fetchProperties(); // Refresh the list
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.message || "Error linking geometry",
        severity: "error",
      });
    }
  };

  const handleAddByCoordinates = async () => {
    if (!newCoordinatesProperty.parcel_id || !newCoordinatesProperty.geometry) {
      setSnackbar({
        open: true,
        message: "Parcel ID and GeoJSON geometry are required.",
        severity: "error",
      });
      return;
    }

    try {
      const res = await fetch("/api/gis/from-coordinates/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newCoordinatesProperty),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || "Failed to create property from coordinates"
        );
      }

      setSnackbar({
        open: true,
        message: "Property created successfully from coordinates",
        severity: "success",
      });
      setShowCoordinatesForm(false);
      setNewCoordinatesProperty({ parcel_id: "", geometry: "" });
      fetchProperties();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.message || "Error creating property",
        severity: "error",
      });
    }
  };

  const handleCsvUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("file", file);

    setUploadingCsv(true);
    try {
      const response = await fetch("/api/gis/upload-csv-file", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload CSV file");
      }

      await response.json();
      setSnackbar({
        open: true,
        message: "CSV file uploaded successfully",
        severity: "success",
      });

      fetchProperties();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "Error uploading CSV file",
        severity: "error",
      });
    } finally {
      setUploadingCsv(false);
    }
  };

  const handleKmlUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("file", file);

    setUploadingKml(true);
    try {
      const response = await fetch("/api/gis/upload-kml-file", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload GIS file");
      }

      await response.json();
      setSnackbar({
        open: true,
        message: "GIS file uploaded successfully",
        severity: "success",
      });

      fetchProperties();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "Error uploading GIS file",
        severity: "error",
      });
    } finally {
      setUploadingKml(false);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Container maxWidth={false}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" component="h1">
            GIS Property Manager
          </Typography>
          <Box>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={uploadingCsv}
              sx={{ m: 1 }}
            >
              Import CSV
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={handleCsvUpload}
              />
            </Button>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={uploadingKml}
              sx={{ m: 1 }}
            >
              Upload GIS File
              <input
                type="file"
                hidden
                accept=".kml,.kmz"
                onChange={handleKmlUpload}
              />
            </Button>
            <Button
              variant="contained"
              onClick={() => setShowCoordinatesForm(true)}
              sx={{ m: 1 }}
            >
              Add by Coordinates
            </Button>
          </Box>
        </Box>

        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Parcel ID</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((prop) => (
                <TableRow
                  key={prop.parcel_id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {prop.parcel_id}
                  </TableCell>
                  <TableCell>{prop.site_address}</TableCell>
                  <TableCell>{prop.owner_names?.join(", ")}</TableCell>
                  <TableCell>
                    <Button
                      onClick={() => navigate(`/admin/gis/${prop.parcel_id}`)}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      View
                    </Button>
                    <Button
                      onClick={() => handleEdit(prop)}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => {
                        setDestinationParcelId(prop.parcel_id);
                        setShowLinkDialog(true);
                      }}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      Link Geometry
                    </Button>
                    <Button
                      onClick={() => handleDelete(prop.parcel_id)}
                      size="small"
                      color="error"
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
      <Dialog
        open={showCoordinatesForm}
        onClose={() => setShowCoordinatesForm(false)}
      >
        <DialogTitle>Add Property by Coordinates</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Parcel ID"
            fullWidth
            value={newCoordinatesProperty.parcel_id}
            onChange={(e) =>
              setNewCoordinatesProperty({
                ...newCoordinatesProperty,
                parcel_id: e.target.value,
              })
            }
          />
          <TextField
            margin="dense"
            label="GeoJSON Geometry"
            fullWidth
            multiline
            rows={6}
            value={newCoordinatesProperty.geometry}
            onChange={(e) =>
              setNewCoordinatesProperty({
                ...newCoordinatesProperty,
                geometry: e.target.value,
              })
            }
            placeholder='e.g., {"type": "Polygon", "coordinates": [[...]]}'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCoordinatesForm(false)}>Cancel</Button>
          <Button onClick={handleAddByCoordinates}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showLinkDialog} onClose={() => setShowLinkDialog(false)}>
        <DialogTitle>Link Geometry</DialogTitle>
        <DialogContent>
          <Typography>
            Link geometry from a source property to:{" "}
            <strong>{destinationParcelId}</strong>
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="source-parcel-select-label">
              Source Parcel
            </InputLabel>
            <Select
              labelId="source-parcel-select-label"
              value={sourceParcelId}
              label="Source Parcel"
              onChange={(e) => setSourceParcelId(e.target.value)}
            >
              {properties
                .filter((p) => p.parcel_id !== destinationParcelId) // Don't allow linking to itself
                .map((p) => (
                  <MenuItem key={p.parcel_id} value={p.parcel_id}>
                    {p.parcel_id} {p.site_address ? `(${p.site_address})` : ""}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLinkDialog(false)}>Cancel</Button>
          <Button onClick={handleLinkGeometry} disabled={!sourceParcelId}>
            Link
          </Button>
        </DialogActions>
      </Dialog>

      {editingProperty && (
        <Dialog
          open={!!editingProperty}
          onClose={() => setEditingProperty(null)}
        >
          <DialogTitle>Edit Property</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Parcel ID"
              fullWidth
              value={editingProperty.parcel_id}
              disabled
            />
            <TextField
              margin="dense"
              label="Site Address"
              fullWidth
              value={editingProperty.site_address || ""}
              onChange={(e) =>
                setEditingProperty({
                  ...editingProperty,
                  site_address: e.target.value,
                })
              }
            />
            <TextField
              margin="dense"
              label="Owner Names"
              fullWidth
              value={editingProperty.owner_names?.join(", ") || ""}
              onChange={(e) =>
                setEditingProperty({
                  ...editingProperty,
                  owner_names: e.target.value.split(",").map((s) => s.trim()),
                })
              }
            />
            {/* Add other fields as needed */}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingProperty(null)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
